import { Link } from "react-router-dom";
import SeriesNavigation from "../../components/SeriesNavigation";
import FadeInSection from "../../components/FadeInSection";
import { media } from "../../data/navigationData";
import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";

export default function NewcastleHerald() {
  const newcastleHeraldSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Newcastle Herald: Stroke drug breakthrough for patients",
    "url": "https://www.thrombio.com.au/media/newcastleherald",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Media",
      "description": "Newcastle Herald article on stroke drug breakthrough"
    }
  };

  return (
    <>
      <SEO
        title="Newcastle Herald: Stroke drug breakthrough for patients"
        description="Newcastle at the centre of world-first stroke breakthrough as patients describe recovery as 'nothing short of miraculous’"
        keywords={['Newcastle Herald', 'John Hunter Hospital', 'Hunter Medical Research', 'Red Blood Cell Roadblocks', 'TBO-309', 'Stroke Research', 'Prof Shaun Jackson', 'Microcirculation', 'Microclots', 'Red cell aggregates', 'Stroke', 'TBO-309', 'Stroke recovery', 'Blood vessel blockage', 'Anti-clotting drug', 'Stroke treatment', 'Novel therapeutics', 'Anti-platelet', 'Clinical trial', 'Platelet inhibitor', 'Sydney-developed drug']}
        ogImage="/newcastleheraldpaper.png"
        schema={newcastleHeraldSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="newcastle-herald" />

      <div className="bg-primary">
        <div className="page-width px-6 md:px-20 pt-[18px] pb-24">
          <nav className="font-sans text-[1.8rem] font-normal leading-[2.3rem]" aria-label="Breadcrumb">
            <Link to="/media" className="text-white hover:text-primary-red transition-colors">
              Media
            </Link>
            <span className="mx-1 text-white"> | </span>
            <span className="text-[#5C95A4]">Newcastle Herald: Stroke drug breakthrough for patients</span>
          </nav>
        </div>

        <div className="page-width px-6 md:px-20 pb-16 text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 mb-20 lg:items-end">
            <FadeInSection>
              <div className="flex flex-col items-center lg:items-start lg:flex-shrink-0 lg:justify-end">
                <a target="_blank" rel="noreferrer" href="https://www.newcastleherald.com.au/story/9232857/stroke-breakthrough-with-thrombios-anti-clotting-drug-tbo-309/">
                  <img src="/newcastleheraldlogo.png" alt="Newcastle Herald Logo" className="md:h-[105px] w-auto" />
                </a>
              </div>
            </FadeInSection>

            <FadeInSection className="delay-500">
              <div className="flex-1 w-full max-w-[600px] flex justify-center self-center lg:self-end">
                <a target="_blank" rel="noreferrer" href="https://www.newcastleherald.com.au/story/9232857/stroke-breakthrough-with-thrombios-anti-clotting-drug-tbo-309/">
                  <img src="/newcastleheraldpaper.png" alt="Newcastle Herald Article" className="w-full md:w-[90%] h-auto" />
                </a>
              </div>
            </FadeInSection>
          </div>

          <div className="flex flex-col gap-6 mb-[30px] pt-2">
            <FadeInSection className="delay-1000">
              <div className="text-[2.4rem] leading-[3.3rem] tracking-[.042rem] text-center">
                <div className='font-light italic'>"Honestly, I thought I was going to die."</div>
                <div className='font-light italic mt-1'>76-year-old Alan Johnson, suffered a severe stroke on February 13 that left him unable to speak or move.</div>
                <div className='font-light italic mt-1'>“The recovery was nothing short of miraculous."</div>
                <a target="_blank" rel="noreferrer" href="https://www.newcastleherald.com.au/story/9232857/stroke-breakthrough-with-thrombios-anti-clotting-drug-tbo-309/" className="font-medium text-[#005aab]">Newcastle Herald</a>
              </div>
            </FadeInSection>
            <FadeInSection>
              <div className="text-[2.1rem] leading-[3rem] tracking-[.042rem] mt-12">A revolutionary treatment for stroke trialled in Newcastle has led to remarkable recoveries among patients who faced severe disability.</div>
              <div className="mt-3">The initial results of the clinical drug trial, which involves John Hunter Hospital, showed promising results that could lead to global change in stroke treatment.</div>
              <div className="mt-3">Patients have shown improved recoveries in the restoration of speech and movement.</div>
              <div className="mt-3">NSW Premier Chris Minns said it was "great to have this groundbreaking research underway here in the Hunter".</div>
              <div className="mt-3">Toronto's Alan Johnson was among almost 30 patients to have been treated with the drug.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="mt-3">The 76-year-old suffered a severe stroke on February 13 that left him unable to speak or move.</div>
              <div className="my-4"><span className="italic">"Honestly, I thought I was going to die,"</span> he said.</div>
              <div className="mt-3">An ambulance took him to John Hunter Hospital, where doctors confirmed he had a severe ischemic stroke.</div>
              <div className="my-4"><span className="italic">"I had a scan and they found two clots in my brain. They wanted to break up those clots to stop any damage occurring,"</span> Mr Johnson said.</div>
              <div className="my-4">Doctors told his family he was a candidate for biotech company ThromBio's clinical trial with an anti-clotting drug called TBO-309. His wife gave approval for him to join the trial.</div>
              <div className="my-4">He was given the trial drug intravenously, along with a standard therapy.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">The trial drug is designed to break down clots that impede blood flow to the brain during a stroke, while avoiding bleeding complications.</div>
              <div className="my-4">The drug mainly targets disease-causing clots, not the normal healthy clotting process, making it more precise and potentially safer.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4"><span className="italic">"On the first day of the stroke, I couldn't talk or move my right arm and leg,"</span> Mr Johnson said.</div>
              <div className="my-4"><span className="italic">"When I woke up the following morning in intensive care, I could lift my arm and leg off the bed.</span></div>
              <div className="my-4"><span className="italic">"By the afternoon, I could lift my leg as high as I normally would. My arm recovered by the fourth day."</span></div>
              <div className="my-4">Mr Johnson was in hospital for only four days.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4"><span className="italic">"The recovery was nothing short of miraculous. It's amazing,"</span> Mr Johnson said.</div>
              <div className="my-4"><span className="italic">"In hindsight, the doctors and nurses are bloody magicians. I'm almost 100 per cent now. I'm enjoying life.”</span></div>
              <div className="my-4">Normally, most stroke patients don’t fully recover.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Mr Johnson is a member of Probus.</div>
              <div className="my-4"><span className="italic">"I only missed one meeting. When I went to the next meeting, all the blokes said you don't look like you've had a stroke,"</span> he said.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Australian scientists have been developing the new stroke treatment for more than 30 years.</div>
              <div className="my-4">Initial trial results suggest it may significantly reduce long-term disability.</div>
              <div className="my-4">Professor Shaun Jackson, founder of Thrombio, said <span className="italic">"what we're seeing in these early patients is ... the possibility of not just saving lives after stroke, but meaningfully improving how people recover”.</span> he said.</div>
              <div className="my-4">The therapy is part of a phase two clinical trial, but the early results provide impetus for the next stage of the drug's development.</div>
              <div className="my-4"><span className="italic">"If these results continue, this has the potential to change how stroke is treated - not just in Australia, but globally,"</span> Professor Jackson said.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Stroke is among Australia's leading causes of death and disability. About 46,000 people experience a stroke across the country each year.</div>
              <div className="my-4">The Hunter New England health district has almost 2000 cases of people hospitalised with stroke and 250 stroke deaths each year.</div>
              <div className="my-4">Researchers aim to expand the drug trial into more hospitals across Australia and through paramedics.</div>
              <div className="my-4">Newcastle has played a key role in trialling the drug.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Conjoint Professor Chris Levi, the principal investigator for the John Hunter trial, said there was <span className="italic">"optimism about where this treatment could lead".</span></div>
              <div className="my-4"><span className="italic">"For clinicians, seeing patients regain speech and movement so quickly is incredibly powerful,"</span> said Professor Levi, of the University of Newcastle's School of Medicine and Public Health. <span className="italic">"If these results continue, the priority will be expanding access.</span></div>
              <div className="my-4"><span className="italic">"Earlier treatment has the potential to dramatically improve outcomes for many more stroke patients."</span></div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Health Minister Ryan Park said <span className="italic">"this is exactly the kind of work we want happening in NSW".</span></div>
              <div className="my-4"><span className="italic">"These early results are very promising. We look forward to working on ways this trial drug can reach more patients as quickly as possible,"</span> he said.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">Minister for Medical Research David Harris said the trial was <span className="italic">"a great example of what can be achieved"</span> in Newcastle.</div>
            </FadeInSection>
            <FadeInSection>
              <div className="my-4">The trial results are attracting international interest. Professor Parashkev Nachev, a neurologist at University College London, said the trial <span className="italic">"represents an entirely new and innovative approach to the emergency treatment of stroke".</span></div>
              <div className="my-4"><span className="italic">"The potential to transform stroke care is enormous,"</span> he said.</div>
            </FadeInSection>
          </div>

          <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] mt-6">
            <FadeInSection>
              <a target="_blank" rel="noreferrer" href="https://www.newcastleherald.com.au/story/9232857/stroke-breakthrough-with-thrombios-anti-clotting-drug-tbo-309/">
                Read about the patient's stories and the Australian lifesaving drug, TBO-309, <span className="underline hover:text-[#C3EEFD]">here</span>
              </a>
            </FadeInSection>
          </div>
        </div>

      </div>
      <SeriesNavigation
        items={media}
        currentSlug="newcastleherald"
        basePath="/media"
      />
    </>
  );
}